import React, { useContext, useEffect, useRef } from 'react'

import { Form, Input } from 'antd'
import PropTypes from 'prop-types'

/**
 * Editable Table Components
 * Provides inline editing functionality for table cells with real-time validation
 */

export const EditableContext = React.createContext(null)

export const EditableRow = ({ ...props }) => {
  const [form] = Form.useForm()

  return (
    <Form component={false} form={form}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

export const EditableCell = ({
  children,
  dataIndex,
  editable,
  handleSave,
  record,
  title,
  ...restProps
}) => {
  const inputRef = useRef(null)
  const form = useContext(EditableContext)

  // Set initial form values when record changes
  useEffect(() => {
    if (form && record) {
      form.setFieldsValue({ [dataIndex]: record[dataIndex] })
    }
  }, [form, record, dataIndex])

  // Handle input change and save
  const handleChange = async value => {
    try {
      // Update form field
      form.setFieldsValue({ [dataIndex]: value })

      // Trigger validation
      await form.validateFields([dataIndex])

      // Save the value
      handleSave({ ...record, [dataIndex]: value })
    } catch (errInfo) {
      // Validation failed, but we still update the value for real-time feedback
      handleSave({ ...record, [dataIndex]: value })
    }
  }

  // Define validation rules based on the field
  const getValidationRules = () => {
    const rules = []

    // Add specific validation for Bay field
    if (dataIndex === 'bay') {
      rules.push({
        validator: (_, value) => {
          if (!value || value.trim() === '') {
            return Promise.reject(new Error(`${title} is required.`))
          }
          if (!/^\d+$/.test(value.trim())) {
            return Promise.reject(new Error('Bay must contain only digits.'))
          }
          const num = parseInt(value, 10)
          if (num < 1 || num > 99) {
            return Promise.reject(new Error('Bay must be between 1 and 99.'))
          }

          return Promise.resolve()
        },
      })
    }
    // Add specific validation for Hatch Covers field
    else if (dataIndex === 'hatchCovers') {
      rules.push({
        validator: (_, value) => {
          if (!value || value.trim() === '') {
            return Promise.reject(new Error(`${title} is required.`))
          }
          if (!/^\d+$/.test(value.trim())) {
            return Promise.reject(
              new Error('Hatch Covers must contain only digits.')
            )
          }
          const num = parseInt(value, 10)
          if (num < 0 || num > 4) {
            return Promise.reject(
              new Error('Hatch Covers must be between 0 and 4.')
            )
          }

          return Promise.resolve()
        },
      })
    }
    // Default required validation for other fields
    else {
      rules.push({
        message: `${title} is required.`,
        required: true,
        whitespace: true,
      })
    }

    return rules
  }

  let childNode = children

  if (editable) {
    childNode = (
      <Form.Item
        name={dataIndex}
        rules={getValidationRules()}
        style={{ margin: 0 }}
        validateTrigger="onBlur"
      >
        <Input onChange={e => handleChange(e.target.value)} ref={inputRef} />
      </Form.Item>
    )
  }

  return <td {...restProps}>{childNode}</td>
}

EditableCell.propTypes = {
  children: PropTypes.node,
  dataIndex: PropTypes.string,
  editable: PropTypes.bool,
  handleSave: PropTypes.func,
  record: PropTypes.object,
  title: PropTypes.string,
}

export const editableComponents = {
  body: {
    cell: EditableCell,
    row: EditableRow,
  },
}
